import React, { useCallback, useContext, useEffect, useState } from "react";
import i18next from "i18next";
import _, { find } from "lodash";

import { Context } from "states/Store";
import { SUCCESS, ERROR, WARNING, Subscription, FREE_DOSSIER_TYPE_ID_VALUE } from "services/common/Constants";
import EADataGrid from "components/common/datatable/EADataGrid";
import DataTableButton from "components/common/datatable/DataTableButtons";

import { getRules, toggleRuleStatus } from "services/edge/RuleService";
import { getAccountAttributesByBondType, getAccountCategorizedAttributes } from "services/edge/AttributesService";
import { getAccountCalculations } from "../../../services/edge/CalculatorService";
import {
  formatActivityTextFromActivity,
  getCountries,
  hasOption,
} from 'services/common/Utils';
import { rulesListColumns } from "./RulesDataTableUtils";
import RuleCreateDialog from "./dialogs/RuleCreateDialog";
import RuleEditDialog from "./dialogs/RuleEditDialog";
import RuleDeleteDialog from "./dialogs/RuleDeleteDialog";

import useStyles from "style/js-style/containers/administration/rules/RulesDataTableStyle";
import { getRuleActionTypes, RulesContext, isExistingRuleToActivateHaveErrors,
 isActionsWithWarning} from "services/common/RulesUtils";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";
import {CalculationNature} from "../../../services/common/CalculatorUtils";
import { getAccountRequirements } from "services/edge/AccountService";
import { getSourceDataConfigForAccount } from "services/edge/DataHubService";
import { getFinancialStrengthScoreValuesForRulesAndCalculations } from "services/common/DataBlocksUtils";
import ActivationWarningDialog from "./dialogs/ActivationWarningsDialog";
import { convert } from "html-to-text";
import { getContextModel } from '../../../services/edge/ContexteService';

const MetaInfoHeader = ({ length }) => {
  const { classes } = useStyles();

  return (
    <div className={classes.headerMetaInfo}>
      <span className={classes.headerMetaInfoBold}>
        {length > 1
          ? i18next.t("rules.insight1Multiple", { activeRulesCount: length })
          : i18next.t("rules.insight1", { activeRulesCount: length })
        }
      </span>
    </div>
  );
};

function RulesDataTable() {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const [loader, setLoader] = useState(true);

  const [rulesList, setRulesList] = useState([]);
  const [completeRulesList, setCompleteRulesList] = useState([]);

  // TODO: Uncomment when multiple rules deletion is working
  // const [rulesSelectedIds, setRulesSelectedIds] = useState([]);
  const [selectedRules, setSelectedRules] = useState([]);

  const [isReadOnlyOpen, setIsReadOnlyOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [
    isActivationWarningDialogOpen,
    setIsActivationWarningDialogOpen,
  ] = useState(false);

  const [accountCountries, setAccountCountries] = useState([]);

  const [accountActivities, setAccountActivities] = useState([]);

  const [financialStrengthRatings, setFinancialStrengthRatings] = useState([]);
  const [financialStrengthScores, setFinancialStrengthScores] = useState([]);

  const [thirdpartyAttributes, setThirdpartyAttributes] = useState([]);
  const [
    accountCalculatedAttributes,
    setAccountCalculatedAttributes,
  ] = useState([]);
  const [accountIndicators, setAccountIndicators] = useState([]);
  const [dossierTypes, setDossierTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [dossierAttributes, setDossierAttributes] = useState([]);
  const [contextModels, setContextModels] = useState([]);

  const [accountSources, setAccountSources] = useState([]);
  const actionTypes = getRuleActionTypes();

  const [showInduedScores, setShowInduedScores] = useState(false);
  // 'Show' has priority here
  const [showTransparencyScore, setShowTransparencyScore] = useState(false);
  const [enableTransparencyScore, setEnableTransparencyScore] = useState(false);
  const [
    showFinancialStrengthScores,
    setShowFinancialStrengthScores,
  ] = useState(false);
  const [showEsgScores, setShowEsgScores] = useState(false);
  const [showEcovadisScores, setShowEcovadisScores] = useState(false);
  const [showSecurityRatingScores, setShowSecurityRatingScores] = useState(false);
  const [showCreditsafeScores, setShowCreditsafeScores] = useState(false);

  const [showSourceData, setShowSourceData] = useState(false);
  const getTargetedThirpartiesCount = (ruleId) => {
    /*Fake data: make an api call instead*/
    return '6 (10%)';
  };

  const fetchRules = useCallback(async () => {
    try {
      setLoader(true);

      const results = await getRules(state.account.id, null);

      setCompleteRulesList(results.content);

      const mappedResults = results.content.map((entry) => {
        return {
          ...entry,
          id: entry.id,
          name: entry.name,
          thirdParties: getTargetedThirpartiesCount(entry.id),
          status: entry.enabled ? "active" : "inactive",
        };
      });

      // Order list by rule name
      let orderedResults = mappedResults.sort(
        (a, b) => a.name.localeCompare(b.name)
      );

      setRulesList(orderedResults);
      setLoader(false);
    } catch (e) {
      setLoader(false);
      dispatch({ type: 'ALERT', alert: { type: ERROR, msg: e.message } });
      setRulesList([]);
    }
  }, [state.account, dispatch]);

  useEffect(() => {
    if (state.account) {
      async function fetchData() {
        try {
          setAccountActivities(
            state.activityTraductions
              .filter((a) => a.origine === 'DNB')
              .map((activity) => {
                return {
                  value: activity.code,
                  label: formatActivityTextFromActivity(activity),
                  level: activity.level,
                };
              })
              .sort((a, b) => a.label.localeCompare(b.label))
          );

          let distinctCountries = [];
          let allCountries = getCountries(i18next.language);
          for (const [key, value] of Object.entries(allCountries)) {
            distinctCountries.push({"value": key, "label": value});
          }
          const sortedMap = Array.from(distinctCountries).sort((a, b) => a.label.localeCompare(b.label));
          setAccountCountries(sortedMap);
        } catch (e) {
          dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
        }
      }

      if (state.activityTraductions.length) {
        fetchData();
      }
    }
  }, [dispatch, state.account, state.activityTraductions]);

  const fetchAttributes = useCallback(async () => {
    try {
      
      const [results, categorizedResults] = await Promise.all([
        getAccountAttributesByBondType(state.account.id, "thirdparty", null),
        getAccountCategorizedAttributes(state.account.id, null, "thirdparty", null)
      ]);

      const filteredAttributes = results.content.filter(
        (r) => r.type !== 'string'
      );

      setThirdpartyAttributes([...filteredAttributes, ...categorizedResults.content]);
    } catch (error) {
      setThirdpartyAttributes([]);
    }
  }, [state.account]);

  const fetchDossierAttributes = useCallback(async () => {
    try {
      const results = await getAccountAttributesByBondType(
        state.account.id,
        'dossier',
        null
      );
      setDossierAttributes(results.content);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setDossierAttributes([]);
    }
  }, [state.account, dispatch]);

  const fetchCalculations = useCallback(async () => {
    try {
      const results = await getAccountCalculations(state.account.id, null, null);
      let calculatedAttributes = [];
      let indicators = [];

      results.content.forEach(entry => {
        let element = {
            value: entry.id,
            label: entry.name,
            type: entry.resultType,
            targetResourceType: entry.targetResourceType,
            targetResourceKeys: entry.targetResourceKeys,
          };

        if (entry.nature === CalculationNature.INDICATOR){
          indicators.push(element);
        }
        
        if (entry.nature === CalculationNature.CALCULATED_ATTRIBUTE){
          calculatedAttributes.push(element);
        }
      })

      setAccountCalculatedAttributes(calculatedAttributes);
      setAccountIndicators(indicators);
    } catch (error) {
      setAccountCalculatedAttributes([]);
      setAccountIndicators([]);
    }
  }, [state.account]);

  const fetchSourceData = useCallback(async () => {
    try {
      const resSourceData = await getSourceDataConfigForAccount(
        state.account.id,
        dispatch
      );
      setAccountSources(resSourceData.content);
    } catch (e) {
      dispatch({ type: 'ALERT', alert: { type: ERROR, msg: e.message } });
      setAccountSources([]);
    }
  }, [state.account, dispatch]);

  const fetchContextModels = useCallback(async () => {
    try {
      const contextModels = await getContextModel(state.account.id, dispatch);
      setContextModels(contextModels.filter(model => model.active));
    } catch(e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
      setContextModels([]);
    }
  }, [state.account, dispatch]);

  useEffect(() => {
    fetchSourceData();
  }, [fetchSourceData]);

  useEffect(() => {
    if (!_.isEmpty(state.referenceData)) {
      setFinancialStrengthRatings(
        state.referenceData.financialStrengthRating
          ?.map((x) => ({
            label: x.rating,
            value: x.rating,
          }))
          ?.sort((a, b) => a.label.localeCompare(b.label))
      );
      setFinancialStrengthScores(
        getFinancialStrengthScoreValuesForRulesAndCalculations()
      );
    }
  }, [state.referenceData]);

  useEffect(() => {
    if (state.account) {
      fetchAttributes();
      fetchDossierAttributes();
      fetchCalculations();
      fetchContextModels();
    }
  }, [
    state.account,
    fetchAttributes,
    fetchDossierAttributes,
    fetchCalculations,
    fetchContextModels
  ]);

  useEffect(() => {
    setShowInduedScores(
      hasOption(state.accounts, state.account.id, Subscription.INDUED) &&
        hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE)
    );
  }, [state.accounts, state.account.id, hasAnyOfPrivileges]);

  useEffect(() => {
    setShowSourceData(
      hasOption(state.accounts, state.account.id, Subscription.DATA_HUB) &&
        hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DATA_HUB_SOURCE)
    );
  }, [state.accounts, state.account.id, hasAnyOfPrivileges]);

  useEffect(() => {
    const bTransparencyOptionActive = hasOption(
      state.accounts,
      state.account.id,
      Subscription.TRANSPARENCY
    );
    setShowTransparencyScore(bTransparencyOptionActive);

    if (bTransparencyOptionActive) {
      setEnableTransparencyScore(
        hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE)
      );
    } else {
      setEnableTransparencyScore(true);
    }
  }, [state.accounts, state.account.id, hasAnyOfPrivileges]);

  useEffect(() => {
    setShowFinancialStrengthScores(
      hasOption(
        state.accounts,
        state.account.id,
        Subscription.DATABLOCKS_FINANCIAL_STRENGTH
      ) &&
        hasAnyOfPrivileges(
          PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
        )
    );
  }, [hasAnyOfPrivileges, state.account.id, state.accounts]);

  useEffect(() => {
    setShowEsgScores(
      hasOption(
        state.accounts,
        state.account.id,
        Subscription.DATABLOCKS_ESG
      ) && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE)
    );
  }, [hasAnyOfPrivileges, state.account.id, state.accounts]);

  useEffect(() => {
    setShowEcovadisScores(
      hasOption(state.accounts, state.account.id, Subscription.ECOVADIS) &&
        hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE)
    );
  }, [hasAnyOfPrivileges, state.account.id, state.accounts]);

  useEffect(() => {
    setShowSecurityRatingScores(
      hasOption(state.accounts, state.account.id, Subscription.SECURITY_RATING) &&
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE)
    )
  }, [hasAnyOfPrivileges, state.account.id, state.accounts]);

  useEffect(() => {
    setShowCreditsafeScores(
      hasOption(state.accounts, state.account.id, Subscription.CREDITSAFE) &&
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE)
    )
  }, [hasAnyOfPrivileges, state.account.id, state.accounts]);

  useEffect(() => {
    const mappedDossierTypes = [
      {
        value: `${FREE_DOSSIER_TYPE_ID_VALUE}`,
        label: i18next.t("free"),
        code: `${FREE_DOSSIER_TYPE_ID_VALUE}`
      }
    ];

    if (state.dossierTypes && !_.isEmpty(state.dossierTypes)) {
      state.dossierTypes.forEach((dt) => {
        mappedDossierTypes.push({
          value: `${dt.id}`,
          label: dt.libelle,
          code: `${dt.code}`,
        });
      });
    }

    setDossierTypes(mappedDossierTypes);
  }, [state.dossierTypes]);

  useEffect(() => {
    async function getRequirements() {
      const results = await getAccountRequirements(state.account.id, dispatch);
      setDocumentTypes(
        results.content.map((x) => {
          return {
            value: `${x?.documentId}`,
            label: `${x?.descriptionFr}`,
            code: x?.code,
            properties: x?.properties?.map((property) => {
              return {
                id: property?.id,
                label: convert(property?.name),
                type: property?.type,
                valueOptions: property?.options || []
              };
            }) || []
          }
        }).sort((a, b) => a.label.localeCompare(b.label))
      );
    }

    if (state.account) {
      getRequirements();
    }
  }, [dispatch, state.account]);

  useEffect(() => {
    if (state.account) {
      fetchRules();
    }
  }, [state.account, fetchRules]);

  const openDeleteDialog = (ruleId) => {
    setIsDeleteDialogOpen(true);
    setSelectedRules(
      /**
       * Actually, there is no need to filter here as only one single rule can be deleted.
       * */
      rulesList.filter(
        (rule) => rule.id === ruleId
      )
    );
  };

  const openEditDialog = (ruleId) => {
    setIsEditDialogOpen(true);
    setSelectedRules([
      rulesList.find((rule) => rule.id === ruleId),
    ]);
  };

  const openReadDialog = (ruleId) => {
    setIsReadOnlyOpen(true);
    setSelectedRules([
      rulesList.find((rule) => rule.id === ruleId),
    ]);
  };

  const openActivationWarningDialog = (ruleId) => {
    setIsActivationWarningDialogOpen(true);
    setSelectedRules([
      rulesList.find((rule) => rule.id === ruleId),
    ]);
  }

  const onToggleRuleStatus = async (ruleId) => {
    let ruleToUpdate = find(completeRulesList, { id: ruleId });
    if(!ruleToUpdate) {
      fetchRules();
      return;
    }

    if (!ruleToUpdate.enabled && isExistingRuleToActivateHaveErrors(ruleToUpdate.actions)) {
      openEditDialog(ruleId)
    } else if (!ruleToUpdate.enabled && isActionsWithWarning(ruleToUpdate.actions, state.dossierTypes)) {
      openActivationWarningDialog(ruleId)
    } else {
      toggleRuleStatusCall(ruleToUpdate);
    }
  };

  const toggleRuleStatusCall = async (ruleToUpdate) => {
    try {
      await toggleRuleStatus(state.account.id, ruleToUpdate.id, null);
      dispatch({
        type: 'ALERT',
        alert: {
          type: ruleToUpdate.enabled ? WARNING : SUCCESS,
          msg: i18next.t(`rules.notifications.${ruleToUpdate.enabled ? "deactivate" : "activate"}`, { name: `${ruleToUpdate.name}` })
        }
      });
      fetchRules();
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: err?.code || 'INTERNAL_ERROR' },
      });
    }
  };

  const context = React.useMemo(
    () => ({
      dossierTypes: dossierTypes,
      actionTypes: isReadOnlyOpen ? actionTypes : actionTypes?.filter(action => {
        if(action.value.includes("FINANCIAL_STRENGTH")) {
          return (PrivilegeEnum.PRIVILEGE_EDIT_FINANCIAL_STRENGTH_SOURCE)
          && hasOption(state.accounts, state.account.id, Subscription.DATABLOCKS_FINANCIAL_STRENGTH)
        }
        if(action.value.includes("DNB_ESG")) {
          return hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DNB_ESG_SOURCE)
          && hasOption(state.accounts, state.account.id, Subscription.DATABLOCKS_ESG)
        }
        return true;
      }),
      documentTypes: documentTypes,
      activities: accountActivities,
      countries: accountCountries,
      thirdpartyAttributes: thirdpartyAttributes,
      dossierAttributes: dossierAttributes,
      calculatedAttributes: accountCalculatedAttributes,
      thirdPartyIndicators: accountIndicators,
      sourceData: accountSources,
      financialStrengthScores: financialStrengthScores,
      financialStrengthRatings: financialStrengthRatings,
      showInduedScores: showInduedScores,
      showTransparencyScore: showTransparencyScore,
      showSourceData: showSourceData,
      enableTransparencyScore: enableTransparencyScore,
      showFinancialStrengthScores: showFinancialStrengthScores,
      showEsgScores: showEsgScores,
      showEcovadisScores: showEcovadisScores,
      showSecurityRatingScores: showSecurityRatingScores,
      showCreditsafeScores: showCreditsafeScores,
      lang: state.lang,
      contextModels
    }),
    [
      dossierTypes,
      actionTypes,
      documentTypes,
      accountActivities,
      accountCountries,
      thirdpartyAttributes,
      dossierAttributes,
      accountCalculatedAttributes,
      accountIndicators,
      accountSources,
      financialStrengthScores,
      financialStrengthRatings,
      showInduedScores,
      showTransparencyScore,
      showSourceData,
      enableTransparencyScore,
      showFinancialStrengthScores,
      showEsgScores,
      showEcovadisScores,
      showSecurityRatingScores,
      showCreditsafeScores,
      hasAnyOfPrivileges,
      state.accounts,
      state.account.id,
      state.lang,
      contextModels,
      isReadOnlyOpen
    ]
  );

  return (
    <RulesContext.Provider value={context}>
      {/* Header */}
      <div className={classes.headerWrapper}>
        <MetaInfoHeader
          length={rulesList.filter((entry) => !!entry.enabled).length}
        />
        <DataTableButton
          onClickHandler={() => setIsCreateDialogOpen(true)}
          label={i18next.t('rules.createButtonLabel')}
          disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_RULE)}
        />
      </div>

      <div className={classes.separate} />

      {/* DataGrid */}
      <div className={classes.dataGridWrapper}>
        <EADataGrid
          loading={loader}
          inputRows={rulesList}
          columns={rulesListColumns(
            openEditDialog,
            openDeleteDialog,
            openReadDialog,
            onToggleRuleStatus,
            hasAnyOfPrivileges
          )}
          hasCustomPagination
          additionnalsLeftComponents={
            <div className={classes.dataGridTitle}>
              {i18next.t('rules.datagridTitle')}
            </div>
          }
          pageSize={10}
        />
      </div>

      {/* PopUp : on create */}
      {isCreateDialogOpen && (
        <RuleCreateDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onValidateSuccess={() => {
            setIsCreateDialogOpen(false);
            fetchRules();
          }}
        />
      )}

      {/* PopUp : on edit */}
      {(isEditDialogOpen || isReadOnlyOpen) && (
        <RuleEditDialog
          rule={find(completeRulesList, { id: selectedRules[0].id })}
          isOpen={isEditDialogOpen || isReadOnlyOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setIsReadOnlyOpen(false);
          }}
          onValidateSuccess={() => {
            setIsEditDialogOpen(false);
            setIsReadOnlyOpen(false);
            fetchRules();
          }}
          onDeleteOpen={() => setIsDeleteDialogOpen(true)}
          readOnly={isReadOnlyOpen}
        />
      )}

      {/* PopUp : on delete */}
      {isDeleteDialogOpen && (
        <RuleDeleteDialog
          rule={selectedRules[0]}
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onValidateSuccess={() => {
            setIsDeleteDialogOpen(false);
            setIsEditDialogOpen(false);
            fetchRules();
          }}
        />
      )}

      {/* PopUp : on activation warning */}
      {isActivationWarningDialogOpen && (
        <ActivationWarningDialog
          isOpen={isActivationWarningDialogOpen}
          onClose={() => setIsActivationWarningDialogOpen(false)}
          onValidateSuccess={() => {
            setIsActivationWarningDialogOpen(false);
            toggleRuleStatusCall(selectedRules[0]);
          }}
          source="indueD"
        />
      )}
    </RulesContext.Provider>
  );
}

export default RulesDataTable;
